import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Row } from 'antd';
import {
	BellFilled,
	DownOutlined,
	LoadingOutlined,
	StarFilled,
	UpOutlined,
} from '@ant-design/icons';
import { fetchWorkOrders } from '../../thunks/work_orders_thunks_wrapper';
import { getEntitiesByIdWithSearch, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { REMINDER_STATUS, ReminderStatus } from '../../data/workOrderStarLabels';
import useWorkOrderStarSocket, {
	isReminderDueOrReconnect,
} from '../../hooks/useWorkOrderStarSocket';
import TableFooter from '../WorkOrderTable/TableFooter';
import {
	TRACKED_ACTIVE_REMINDERS_COLLECTION,
	TRACKED_UPCOMING_REMINDERS_COLLECTION,
} from '../../constants/WorkOrderReminderCollections';

// The Tracked view's own search is the "Tracked" group (no reminder); Active and Upcoming are
// separate searches with the same filters and sort, each paged on its own.
export const TRACKED_GROUP_PRE_APPLIED_FILTERS = {
	starredByCurrentUser: true,
	reminderStatusForCurrentUser: REMINDER_STATUS.NONE,
};

// With reminders off (Work-Order-Reminders) the Tracked view is one list of every tracked work
// order, so the "no reminder" scope is cleared — it would hide ones with a saved reminder.
export const TRACKED_PRE_APPLIED_FILTERS = {
	starredByCurrentUser: true,
	reminderStatusForCurrentUser: undefined,
};

// Same first page as the work order list itself (parseURL's default).
const GROUP_INITIAL_PAGINATION = { current: 1, pageSize: 50 };

interface TableBodyProps {
	data: any[];
	fetching: boolean;
	pagination: any;
	updatePagination: (pagination: any) => void;
	filters: any;
	sorting: any;
	renderTable: (data: any[], fetching: boolean, collectionName?: string) => React.ReactNode;
}

interface OwnProps extends TableBodyProps {
	userType: string;
}

interface Props extends OwnProps {
	activeCollection: any;
	upcomingCollection: any;
	activeData: any[];
	upcomingData: any[];
	starWriteCount: number;
	fetchGroup: (collection: string, status: ReminderStatus, pagination, sorting, filters) => void;
}

const GROUP_ICON_CLASS =
	'box-border inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full';

const GroupSection: React.FC<{
	icon: React.ReactNode;
	title: string;
	total: number;
	// While the group's search is in flight its total may be another query's, so it isn't shown.
	loading: boolean;
	pagination: any;
	updatePagination: (pagination: any) => void;
	userType: string;
	children: React.ReactNode;
}> = ({ icon, title, total, loading, pagination, updatePagination, userType, children }) => {
	const [collapsed, setCollapsed] = useState(false);
	return (
		<div className="mt-m bg-white">
			<div
				role="button"
				tabIndex={0}
				onClick={() => setCollapsed((current) => !current)}
				className="flex cursor-pointer items-center gap-xs border-0 border-b border-solid border-grey-4 px-m py-s"
			>
				{icon}
				<span className="h5regular flex flex-1 items-center gap-xs text-character-primary">
					{loading ? (
						<>
							{title}
							<LoadingOutlined className="!flex text-sm text-character-secondary" />
						</>
					) : (
						`${title} (${total})`
					)}
				</span>
				{collapsed ? <DownOutlined /> : <UpOutlined />}
			</div>
			{collapsed ? null : (
				<>
					{children}
					{!loading && total > pagination.pageSize ? (
						<Row className="p-m" justify="end">
							<TableFooter
								updatePagination={updatePagination}
								pagination={pagination}
								userType={userType}
							/>
						</Row>
					) : null}
				</>
			)}
		</div>
	);
};

// Module-level so they outlive the view while the user is on a work order's detail page.
let reconciledStarWriteCount: number | null = null;
let loadedGroupsQueryKey: string | null = null;

// Without the reminder grouping: the base the Active/Upcoming searches add their own status to.
const withoutReminderGrouping = (filters) => ({
	...filters,
	reminderStatusForCurrentUser: undefined,
});

const groupTotal = (collection, data: any[]): number =>
	nullSafeGetOrElse('pagination.total', collection, data.length);

const TrackedReminderGroups: React.FC<Props> = ({
	data,
	fetching,
	pagination,
	updatePagination,
	filters,
	sorting,
	renderTable,
	userType,
	activeCollection,
	upcomingCollection,
	activeData,
	upcomingData,
	starWriteCount,
	fetchGroup,
}) => {
	const mountedOnTrackedRef = useRef(!!(filters && filters.starredByCurrentUser));
	const sawTrackedFetchRef = useRef(false);
	const baseFilters = withoutReminderGrouping(filters || {});
	const queryKey = JSON.stringify({ baseFilters, sorting });
	const activePagination = nullSafeGetOrElse(
		'pagination',
		activeCollection,
		GROUP_INITIAL_PAGINATION
	);
	const upcomingPagination = nullSafeGetOrElse(
		'pagination',
		upcomingCollection,
		GROUP_INITIAL_PAGINATION
	);

	const fetchActive = (groupPagination) =>
		fetchGroup(
			TRACKED_ACTIVE_REMINDERS_COLLECTION,
			REMINDER_STATUS.ACTIVE,
			groupPagination,
			sorting,
			baseFilters
		);
	const fetchUpcoming = (groupPagination) =>
		fetchGroup(
			TRACKED_UPCOMING_REMINDERS_COLLECTION,
			REMINDER_STATUS.UPCOMING,
			groupPagination,
			sorting,
			baseFilters
		);

	// New filters or sort restart the groups at page 1; returning with the same ones keeps pages.
	useEffect(() => {
		// Wait for WorkOrderTable to resolve the view's filters, which always carry the scope.
		if (!baseFilters.starredByCurrentUser) return;
		const sameQuery = loadedGroupsQueryKey === queryKey;
		loadedGroupsQueryKey = queryKey;
		fetchActive(sameQuery ? activePagination : GROUP_INITIAL_PAGINATION);
		fetchUpcoming(sameQuery ? upcomingPagination : GROUP_INITIAL_PAGINATION);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [queryKey]);

	// Re-read all three groups on their current pages.
	const refetchGroups = () => {
		fetchActive(activePagination);
		fetchUpcoming(upcomingPagination);
		if (pagination) updatePagination(pagination);
	};

	// Any star write can move rows between groups, including writes made while the view was unmounted,
	// so compare against the last reconciled count rather than the one at mount.
	useEffect(() => {
		const lastReconciled = reconciledStarWriteCount;
		reconciledStarWriteCount = starWriteCount;
		// The view's first visit loads everything fresh.
		if (lastReconciled === null || starWriteCount === lastReconciled) return;
		refetchGroups();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [starWriteCount]);

	// A reminder coming due moves its row from Upcoming to Active without changing the star, so the
	// star-write re-read above misses it; other changes made elsewhere arrive as star writes. A
	// reconnect re-reads, since messages may have been missed.
	useWorkOrderStarSocket((message) => {
		if (isReminderDueOrReconnect(message)) refetchGroups();
	});

	const activeFetching = nullSafeGetOrElse('fetching', activeCollection, false);
	const upcomingFetching = nullSafeGetOrElse('fetching', upcomingCollection, false);

	// A re-read can empty a group's current page (and an empty group hides its pager), so step back
	// to its new last page.
	const stepBackIfPastLastPage = (
		rows: any[],
		isFetching: boolean,
		groupPagination,
		total: number,
		refetch: (nextPagination) => void
	) => {
		if (isFetching || rows.length > 0 || !total || !groupPagination) return;
		const lastPage = Math.max(1, Math.ceil(total / groupPagination.pageSize));
		if (groupPagination.current > lastPage) refetch({ ...groupPagination, current: lastPage });
	};
	const activeTotal = groupTotal(activeCollection, activeData);
	const upcomingTotal = groupTotal(upcomingCollection, upcomingData);
	const trackedTotal = nullSafeGetOrElse('total', pagination, 0);
	useEffect(() => {
		stepBackIfPastLastPage(activeData, activeFetching, activePagination, activeTotal, fetchActive);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeFetching, activeData.length, activeTotal, activePagination.current]);
	useEffect(() => {
		stepBackIfPastLastPage(
			upcomingData,
			upcomingFetching,
			upcomingPagination,
			upcomingTotal,
			fetchUpcoming
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [upcomingFetching, upcomingData.length, upcomingTotal, upcomingPagination.current]);
	// The Tracked group reads the view's shared work order list, which still holds the previous
	// view's rows and total when the user switches here: its filters change at once, but the search
	// is debounced. So until this mount has seen a search run it shows neither — unless the list
	// already held a Tracked search when it mounted (back from a work order, kept with `preserve`).
	if (fetching) sawTrackedFetchRef.current = true;
	const trackedIsStale = !mountedOnTrackedRef.current && !sawTrackedFetchRef.current;
	const trackedLoading = fetching || trackedIsStale;
	const trackedData = trackedIsStale ? [] : data || [];
	const trackedPage = nullSafeGetOrElse('current', pagination, 1);
	useEffect(() => {
		stepBackIfPastLastPage(trackedData, trackedLoading, pagination, trackedTotal, updatePagination);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [trackedLoading, trackedData.length, trackedTotal, trackedPage]);
	const isEmpty =
		!trackedLoading &&
		!activeFetching &&
		!upcomingFetching &&
		activeData.length === 0 &&
		upcomingData.length === 0 &&
		trackedData.length === 0;

	if (isEmpty) {
		return <div className="mt-m bg-white">{renderTable([], false)}</div>;
	}

	return (
		<>
			{activeData.length > 0 ? (
				<GroupSection
					icon={
						<span className={`${GROUP_ICON_CLASS} animate-reminderGlow bg-character-danger`}>
							<BellFilled className="!flex origin-top animate-bellRing text-xs !text-white" />
						</span>
					}
					title="Active Reminders"
					total={activeTotal}
					loading={activeFetching}
					pagination={activePagination}
					updatePagination={fetchActive}
					userType={userType}
				>
					{renderTable(activeData, activeFetching, TRACKED_ACTIVE_REMINDERS_COLLECTION)}
				</GroupSection>
			) : null}
			{upcomingData.length > 0 ? (
				<GroupSection
					icon={
						<span className={`${GROUP_ICON_CLASS} bg-calendulaGold-8`}>
							<BellFilled className="!flex text-xs !text-white" />
						</span>
					}
					title="Upcoming Reminders"
					total={upcomingTotal}
					loading={upcomingFetching}
					pagination={upcomingPagination}
					updatePagination={fetchUpcoming}
					userType={userType}
				>
					{renderTable(upcomingData, upcomingFetching, TRACKED_UPCOMING_REMINDERS_COLLECTION)}
				</GroupSection>
			) : null}
			{trackedData.length > 0 || trackedLoading ? (
				<GroupSection
					icon={
						<span className={`${GROUP_ICON_CLASS} bg-calendulaGold-5`}>
							<StarFilled className="text-xs !text-white" />
						</span>
					}
					title="Tracked"
					total={nullSafeGetOrElse('total', pagination, trackedData.length)}
					loading={trackedLoading}
					pagination={pagination}
					updatePagination={updatePagination}
					userType={userType}
				>
					{renderTable(trackedData, trackedLoading)}
				</GroupSection>
			) : null}
		</>
	);
};

const mapStateToProps = (state) => {
	const workOrders = state.work_orders;
	const recordsFor = (collectionName: string) =>
		getEntitiesByIdWithSearch(
			nullSafeGetOrElse('recordsIds', workOrders[collectionName], []),
			workOrders
		);
	return {
		activeCollection: workOrders[TRACKED_ACTIVE_REMINDERS_COLLECTION],
		upcomingCollection: workOrders[TRACKED_UPCOMING_REMINDERS_COLLECTION],
		starWriteCount: nullSafeGetOrElse('work_order_star_labels.starWriteCount', state, 0),
		activeData: recordsFor(TRACKED_ACTIVE_REMINDERS_COLLECTION),
		upcomingData: recordsFor(TRACKED_UPCOMING_REMINDERS_COLLECTION),
	};
};

const mapDispatchToProps = (dispatch, ownProps: OwnProps) => ({
	fetchGroup: (collection: string, status: ReminderStatus, pagination, sorting, filters) =>
		dispatch(
			fetchWorkOrders(ownProps.userType)(
				false,
				null,
				null,
				collection,
				{ ...filters, reminderStatusForCurrentUser: status },
				pagination,
				sorting,
				{},
				true
			)
		),
});

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(TrackedReminderGroups as any) as React.ComponentType<OwnProps>;
